import cx from 'classnames'
import Styles from './ImageEditor.module.scss'
import { Close, Trash2 } from 'blaze-ui/icons'
import { Upload5 } from 'blaze-ui/icons/20'
import { Button } from 'blaze-ui/Button/Button'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Text } from 'blaze-ui/Text/Text'

export interface BrandKitLogoOption {
  id: number
  url: string
  name?: string
}

interface LogoHotspotPopoverViewProps {
  brandKitLogos: BrandKitLogoOption[]
  replaceLogoUrl: string
  onSelectBrandKitLogoUrl: (url: string) => void
  onPickLogoUpload: () => void
  onConfirm: () => void
  onRemoveLogo: () => void
  close: () => void
}

const ariaLabelForBrandKitLogo = (logo: BrandKitLogoOption) =>
  logo.name?.trim() ? `Use brand kit logo: ${logo.name.trim()}` : 'Use brand kit logo'

export const LogoHotspotPopoverView = ({
  brandKitLogos,
  replaceLogoUrl,
  onSelectBrandKitLogoUrl,
  onPickLogoUpload,
  onConfirm,
  onRemoveLogo,
  close,
}: LogoHotspotPopoverViewProps) => {
  const brandKitUrls = new Set(brandKitLogos.map((l) => l.url))
  const hasAnyBrandKitLogo = brandKitLogos.length > 0
  const uploadSelected = Boolean(replaceLogoUrl && (!hasAnyBrandKitLogo || !brandKitUrls.has(replaceLogoUrl)))
  const canConfirm = Boolean(replaceLogoUrl)

  return (
    <div className={cx(Styles.imageHotspotPopover, Styles.logoHotspotPopoverLayout)}>
      <IconButton
        variant="secondary"
        size="sm"
        icon={Close}
        aria-label="Close"
        onPress={close}
        className={Styles.editTextClose}
      />
      <Text variant="primary" color="var(--dark-90)" className={Styles.logoHotspotTitle}>
        Select which you want to use
      </Text>
      <div className={Styles.logoHotspotScrollArea}>
        {brandKitLogos.length > 0 && (
          <div className={Styles.logoHotspotSection}>
            <span className={Styles.logoHotspotSectionLabel}>Your Brand Kit logo</span>
            <div
              className={Styles.logoHotspotLogosColumn}
              role="group"
              aria-label="Brand kit logos"
            >
              {brandKitLogos.map((logo) => {
                const selected = replaceLogoUrl === logo.url
                return (
                  <button
                    key={logo.id}
                    type="button"
                    className={cx(
                      Styles.logoHotspotPickTile,
                      Styles.logoHotspotVariantTile,
                      Styles.logoHotspotVariantTileRegular,
                      Styles.logoHotspotPickTileBrand,
                      selected && Styles.logoHotspotPickTileSelected,
                    )}
                    onClick={() => onSelectBrandKitLogoUrl(logo.url)}
                    aria-label={ariaLabelForBrandKitLogo(logo)}
                  >
                    <img src={logo.url} alt="" className={Styles.logoHotspotPickTileImg} />
                  </button>
                )
              })}
            </div>
          </div>
        )}
        <div className={Styles.logoHotspotSection}>
          <button
            type="button"
            className={cx(
              Styles.logoHotspotPickTile,
              Styles.logoHotspotPickTileUpload,
              uploadSelected && Styles.logoHotspotPickTileSelected,
            )}
            onClick={onPickLogoUpload}
            aria-label="Upload your own logo"
          >
            {uploadSelected ? (
              <img src={replaceLogoUrl} alt="" className={Styles.logoHotspotPickTileImgUpload} />
            ) : (
              <span className={Styles.logoHotspotUploadInner}>
                <Upload5 size={16} color="var(--dark-90)" />
                <span className={Styles.logoHotspotUploadLabel}>Upload your own</span>
              </span>
            )}
          </button>
        </div>
      </div>
      <div className={Styles.logoHotspotFooter}>
        <Button variant="tertiary" size="md" frontIcon={Trash2} onPress={onRemoveLogo} aria-label="Remove logo">
          Remove logo
        </Button>
        <Button variant="primary" size="md" onPress={onConfirm} isDisabled={!canConfirm} aria-label="Confirm">
          Confirm
        </Button>
      </div>
    </div>
  )
}
